import { Sparkles, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { DateTime } from 'luxon';
import { useAuthStore } from '../../hooks/useAuth';
import { resolveEffectiveTimeZone } from '../../utils/timezoneDisplay';

interface AISlotSuggestionsProps {
    /**
     * Called when the user selects a suggestion.
     * All four arguments are required for scheduling to correctly resolve DST occurrences.
     *   start    — warehouse-local wall-clock  "YYYY-MM-DDTHH:mm"
     *   end      — warehouse-local wall-clock  "YYYY-MM-DDTHH:mm"
     *   startUtc — canonical UTC ISO string    (unique DST occurrence key)
     *   endUtc   — canonical UTC ISO string
     */
    onSelect?: (start: string, end: string, startUtc: string, endUtc: string) => void;
    /** Warehouse IANA timezone. Falls back to user.timeZone from auth store. */
    warehouseTimeZone?: string;
}

interface Slot {
    time: string;
    localDate: string;      // "YYYY-MM-DD" in warehouse timezone
    startLocal: string;     // "YYYY-MM-DDTHH:mm" in warehouse timezone
    endLocal: string;       // "YYYY-MM-DDTHH:mm" in warehouse timezone
    startUtc: string;       // canonical UTC ISO — unique occurrence key
    endUtc: string;         // canonical UTC ISO
    confidence: number;
    tags: string[];
    description: string;
}

const SUGGESTION_TIMES: Array<{
    hour: number;
    minute: number;
    label: string;
    confidence: number;
    tags: string[];
    description: string;
}> = [
    {
        hour: 8,
        minute: 0,
        label: '08:00 - 10:00',
        confidence: 98,
        tags: ['Fastest Turnaround', 'Morning Slot'],
        description: 'Crowd logic suggests low yard traffic.',
    },
    {
        hour: 13,
        minute: 0,
        label: '13:00 - 15:00',
        confidence: 94,
        tags: ['Avoids Shift Change'],
        description: 'Optimal for standard loads.',
    },
    {
        hour: 19,
        minute: 0,
        label: '19:00 - 21:00',
        confidence: 89,
        tags: ['Off-Peak Option'],
        description: 'Quiet hours, immediate docking likely.',
    },
];

const DURATION_MS = 7200000; // 2 hours exactly, matching booking engine

const AISlotSuggestions = ({ onSelect, warehouseTimeZone }: AISlotSuggestionsProps) => {
    const { user } = useAuthStore();

    // Authoritative warehouse timezone — from prop, then auth store. No hardcoded fallback.
    const whTz = warehouseTimeZone || user?.timeZone;

    // Build slots only if we have an authoritative timezone.
    // If whTz is absent, render no suggestions rather than invent browser-local ones.
    const suggestions: Slot[] = (() => {
        if (!whTz) return [];

        // "Tomorrow in the warehouse's local calendar" — derived from the current UTC instant
        // converted to the warehouse timezone. Never uses browser-local Date getters.
        const effectiveNowTz = resolveEffectiveTimeZone(whTz) || whTz;
        const tomorrowDateISO = DateTime.now().setZone(effectiveNowTz).plus({ days: 1 }).toISODate()!;
        const effectiveTomorrowTz = resolveEffectiveTimeZone(whTz, tomorrowDateISO) || whTz;
        const [year, month, day] = tomorrowDateISO.split('-').map(Number);

        const result: Slot[] = [];

        for (const def of SUGGESTION_TIMES) {
            // Build the start instant anchored in the warehouse timezone.
            // DateTime.fromObject resolves DST-ambiguous times to the first (pre-transition)
            // occurrence — consistent with generateOperatingSlots semantics.
            const startDt = DateTime.fromObject(
                { year, month, day, hour: def.hour, minute: def.minute },
                { zone: effectiveTomorrowTz }
            );

            // Skip invalid Luxon instances (e.g. nonexistent spring-forward times).
            if (!startDt.isValid) continue;

            const endDt = startDt.plus({ milliseconds: DURATION_MS });
            const startUtc = startDt.toUTC().toISO()!;
            const endUtc = endDt.toUTC().toISO()!;

            result.push({
                time: def.label,
                localDate: tomorrowDateISO,
                startLocal: `${tomorrowDateISO}T${String(def.hour).padStart(2, '0')}:${String(def.minute).padStart(2, '0')}`,
                endLocal: endDt.setZone(effectiveTomorrowTz).toFormat("yyyy-MM-dd'T'HH:mm"),
                startUtc,
                endUtc,
                confidence: def.confidence,
                tags: def.tags,
                description: def.description,
            });
        }

        return result;
    })();

    return (
        <div className="relative group overflow-hidden rounded-xl border border-primary/20 bg-slate-900/40 backdrop-blur-sm p-6 shadow-2xl transition-all hover:border-primary/40">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary/20 blur-[60px] rounded-full pointer-events-none" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-purple-500/10 blur-[60px] rounded-full pointer-events-none" />

            <div className="flex justify-between items-center mb-6 relative z-10">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg border border-primary/20 shadow-[0_0_10px_rgba(168,85,247,0.15)]">
                        <Sparkles className="text-primary h-5 w-5" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-white leading-tight">AI Slot Suggestions</h3>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-medium mt-0.5">
                            Predictive Optimization
                        </p>
                    </div>
                </div>
            </div>

            <div className="space-y-3 relative z-10">
                {suggestions.map((slot, index) => (
                    <motion.button
                        key={slot.startUtc}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => onSelect?.(slot.startLocal, slot.endLocal, slot.startUtc, slot.endUtc)}
                        className="w-full text-left group/card relative border border-slate-700/50 bg-slate-800/30 hover:bg-slate-800/80 hover:border-primary/50 rounded-lg p-3 transition-all duration-200 shadow-sm hover:shadow-[0_0_15px_rgba(168,85,247,0.1)]"
                    >
                        <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                                <span className="text-base font-bold text-slate-100 group-hover/card:text-primary transition-colors">
                                    {slot.time}
                                </span>
                                {index === 0 && (
                                    <span className="text-[10px] font-bold uppercase bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded border border-emerald-500/20">
                                        Best Match
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-1.5 text-xs font-medium">
                                <span className="text-primary">{slot.confidence}% Match</span>
                            </div>
                        </div>

                        <p className="text-xs text-slate-400 mb-3 line-clamp-1">{slot.description}</p>

                        <div className="flex flex-wrap gap-1.5 justify-between items-center">
                            <div className="flex gap-1.5">
                                {slot.tags.map((tag) => (
                                    <span
                                        key={tag}
                                        className="inline-flex items-center gap-1 bg-slate-700/50 border border-slate-600/50 px-1.5 py-0.5 rounded text-[10px] font-medium text-slate-300"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>

                            {onSelect && (
                                <div className="opacity-0 group-hover/card:opacity-100 transition-opacity -mr-1">
                                    <div className="p-1 rounded-full bg-primary/20 text-primary">
                                        <ArrowRight className="w-3.5 h-3.5" />
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.button>
                ))}

                {suggestions.length === 0 && (
                    <p className="text-xs text-slate-500 text-center py-4">
                        Warehouse timezone unavailable — suggestions cannot be generated.
                    </p>
                )}
            </div>

            <div className="mt-4 pt-4 border-t border-white/5 text-center">
                <p className="text-[10px] text-slate-500">
                    Calculated based on real-time yard velocity & historical dwell times.
                </p>
            </div>
        </div>
    );
};

export default AISlotSuggestions;
